if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this._bindGalleryControls();
      }

      hide() {
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this._preloadAllImages();
        this.showActiveMedia();
      }

      _preloadAllImages() {
        this.querySelectorAll('img[data-media-id]').forEach((img) => {
          img.setAttribute('loading', 'eager');
          const src = img.getAttribute('src');
          if (src) {
            const i = new Image();
            i.src = src;
          }
        });
      }

      showActiveMedia() {
        window.pauseAllMedia();
        let mediaId = this.openedBy ? this.openedBy.getAttribute('data-media-id') : null;
        
        let activeMedia = mediaId ? this.querySelector(`[data-media-id="${mediaId}"]`) : null;
        if (!activeMedia && mediaId && mediaId.includes('-')) {
          const rawId = mediaId.split('-').pop();
          activeMedia = this.querySelector(`[data-media-id="${rawId}"]`);
        }
        
        // Fallback to first media in modal if target media is not found
        if (!activeMedia) {
          activeMedia = this.querySelector('[role="document"] [data-media-id]');
        }

        if (!activeMedia) return;

        this.querySelectorAll('[role="document"] [data-media-id]').forEach((element) => {
          element.classList.remove('active');
        });

        activeMedia.classList.add('active');

        const container = this.querySelector('[role="document"]');
        if (container && activeMedia.width) {
          container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;
        }

        this._ensureMediaIsPlayable(activeMedia);
        this._syncGallery();
      }

      _getContentMediaItems() {
        return Array.from(this.querySelector('[role="document"]').children).filter(
          (el) => el.hasAttribute('data-media-id')
        );
      }

      _activateByIndex(index) {
        const items = this._getContentMediaItems();
        if (!items.length) return;
        window.pauseAllMedia();
        const clamped = (index + items.length) % items.length;
        items.forEach((el, i) => el.classList.toggle('active', i === clamped));
        this._ensureMediaIsPlayable(items[clamped]);
        this._syncGallery();
      }

      _ensureMediaIsPlayable(activeMedia) {
        if (!activeMedia || activeMedia.nodeName !== 'DEFERRED-MEDIA') return;

        window.pauseAllMedia();

        // Load the video so its own native controls show (no extra poster play button), but strip autoplay
        // from the template first so loadContent() neither autoplays nor calls play(). It plays on click only.
        const templateVideo = activeMedia.querySelector('template')?.content.querySelector('video');
        if (templateVideo) {
          templateVideo.removeAttribute('autoplay');
          activeMedia.loadContent(false);
        }

        const activeVideo = activeMedia.querySelector('video');
        if (!activeVideo) return;

        activeVideo.autoplay = false;
        activeVideo.removeAttribute('autoplay');
        // With preload="none" Chrome ignores click-to-play until data loads; metadata only, no playback.
        if (activeVideo.preload === 'none') activeVideo.preload = 'metadata';
        activeVideo.controls = true;
        activeVideo.muted = false;
        activeVideo.removeAttribute('muted');
        activeVideo.setAttribute('controls', '');
      }

      _syncGallery() {
        const activeItem = this.querySelector('[role="document"] [data-media-id].active');
        if (!activeItem) return;
        const activeId = activeItem.getAttribute('data-media-id');
        this.querySelectorAll('.rg-modal-thumb').forEach((thumb) => {
          thumb.classList.toggle('rg-modal-thumb--active', thumb.getAttribute('data-media-id') === activeId);
        });

        // scroll active thumb into view
        const activeThumb = this.querySelector(`.rg-modal-thumb[data-media-id="${activeId}"]`);
        if (activeThumb) activeThumb.scrollIntoView({ block: 'nearest', inline: 'center' });

        // show/hide nav arrows
        const items = this._getContentMediaItems();
        const currentIndex = items.findIndex((el) => el.getAttribute('data-media-id') === activeId);
        const prevBtn = this.querySelector('.rg-modal-nav--prev');
        const nextBtn = this.querySelector('.rg-modal-nav--next');
        if (prevBtn) prevBtn.style.opacity = items.length > 1 ? '1' : '0';
        if (nextBtn) nextBtn.style.opacity = items.length > 1 ? '1' : '0';
      }

      _bindGalleryControls() {
        // Stop pointerup from bubbling to the ModalDialog handler which would close the modal
        this.addEventListener('pointerup', (e) => {
          if (e.target.closest('.rg-modal-nav, .rg-modal-thumb, .rg-modal-thumbs')) {
            e.stopImmediatePropagation();
          }
        }, true);

        this.addEventListener('click', (e) => {
          const prev = e.target.closest('.rg-modal-nav--prev');
          const next = e.target.closest('.rg-modal-nav--next');
          const thumb = e.target.closest('.rg-modal-thumb');

          if (prev || next) {
            const items = this._getContentMediaItems();
            const activeItem = items.find((el) => el.classList.contains('active'));
            const currentIndex = activeItem ? items.indexOf(activeItem) : 0;
            this._activateByIndex(prev ? currentIndex - 1 : currentIndex + 1);
          } else if (thumb) {
            const targetId = thumb.getAttribute('data-media-id');
            const items = this._getContentMediaItems();
            const index = items.findIndex((el) => el.getAttribute('data-media-id') === targetId);
            if (index !== -1) this._activateByIndex(index);
          }
        });

        // Swipe support on touch devices
        let touchStartX = 0;
        this.addEventListener('touchstart', (e) => { touchStartX = e.touches[0].clientX; }, { passive: true });
        this.addEventListener('touchend', (e) => {
          const diff = touchStartX - e.changedTouches[0].clientX;
          if (Math.abs(diff) < 40) return;
          const items = this._getContentMediaItems();
          const activeItem = items.find((el) => el.classList.contains('active'));
          const currentIndex = activeItem ? items.indexOf(activeItem) : 0;
          this._activateByIndex(diff > 0 ? currentIndex + 1 : currentIndex - 1);
        }, { passive: true });
      }
    }
  );
}
